"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.convertSegmentationToTupuAkeGeoJson = exports.getPredominantSpecieIdOrThrowError = exports.getTreeSpecieIdOrThrowError = exports.ErrorUnspecified = exports.generateClearingFeature = exports.generatePlantingFeature = void 0;
const helpers_1 = require("@turf/helpers");
const species_queries_1 = require("./models/species/species-queries");
const operation_type_1 = require("./models/tupuake/properties/operation_type");
const establishment_method_1 = require("./models/tupuake/properties/establishment_method");
const clearance_method_1 = require("./models/tupuake/properties/clearance_method");
const forest_type_1 = require("./models/tupuake/properties/forest_type");
const date_1 = require("./operations/date");
const tree_species_1 = require("./models/tupuake/properties/tree_species");
const predominant_species_1 = require("./models/tupuake/properties/predominant_species");
const isPlantingOperation = (properties) => !properties.previous_deforestation_date;
const generatePlantingFeature = (props) => ({
    type: 'Feature',
    properties: {
        OPERATION_TYPE_ID: operation_type_1.operation_types_map.planting.tupuake_id,
        SID: props.sid,
        GLOBAL_ID: null,
        ESTABLISHMENT_METH_CURR_ROTN_ID: props.establishment_method_id,
        FOREST_TYPE_ID: props.forest_type_id,
        PREDOMINANT_SPECIES_ID: props.predominant_specie_id,
        OTHER_PREDOMINANT_SPECIES: null,
        TREE_SPECIES_NAME_ID: props.tree_specie_id,
        OTHER_TREE_SPECIES_NAME: null,
        STEM_COUNT: props.stem_count,
        COMPARTMENT_NUM: null,
        FOREST_NUMBER: null,
        // @ts-ignore I don't know where this came from (does not comply with schema), but don't want to break it
        CLEARANCE_TYPE_ID: 0,
        START_DATE: (0, date_1.getNearlyStartOfYear)(props.established_date),
        END_DATE: (0, date_1.getNearlyEndOfYear)(props.established_date),
        HARVEST_AGE: 0,
    },
    geometry: props.geometry,
});
exports.generatePlantingFeature = generatePlantingFeature;
const generateClearingFeature = (props) => ({
    type: 'Feature',
    properties: {
        OPERATION_TYPE_ID: operation_type_1.operation_types_map.clearing.tupuake_id,
        SID: props.sid,
        GLOBAL_ID: null,
        // @ts-ignore I don't know where this came from (does not comply with schema), but don't want to break it
        ESTABLISHMENT_METH_CURR_ROTN_ID: 0,
        // @ts-ignore I don't know where this came from (does not comply with schema), but don't want to break it
        FOREST_TYPE_ID: 0,
        PREDOMINANT_SPECIES_ID: 0,
        OTHER_PREDOMINANT_SPECIES: null,
        TREE_SPECIES_NAME_ID: 0,
        OTHER_TREE_SPECIES_NAME: null,
        STEM_COUNT: null,
        COMPARTMENT_NUM: null,
        FOREST_NUMBER: null,
        CLEARANCE_TYPE_ID: props.clearance_type_id,
        START_DATE: (0, date_1.getNearlyStartOfYear)(props.previous_deforestation_date),
        END_DATE: (0, date_1.getNearlyEndOfYear)(props.previous_deforestation_date),
        HARVEST_AGE: null,
    },
    geometry: props.geometry,
});
exports.generateClearingFeature = generateClearingFeature;
class ErrorUnspecified extends Error {
    constructor(species, expected_result) {
        super(`Specie ${species} isn't specific enough to deduce the ${expected_result} species, you can select a more specific species to solve this problem`);
        this.name = 'ErrorUnspecified';
    }
}
exports.ErrorUnspecified = ErrorUnspecified;
const getTreeSpecieIdOrThrowError = (species) => {
    var _a;
    const result = (0, species_queries_1.getTreeSpecie)(species);
    if (result === undefined) {
        throw new ErrorUnspecified(species, 'tree');
    }
    return (_a = (0, tree_species_1.getTreeSpeciesByKey)(result)) === null || _a === void 0 ? void 0 : _a.tupuake_id;
};
exports.getTreeSpecieIdOrThrowError = getTreeSpecieIdOrThrowError;
const getPredominantSpecieIdOrThrowError = (species) => {
    var _a;
    const result = (0, species_queries_1.getPredominantSpecies)(species);
    if (result === undefined) {
        throw new ErrorUnspecified(species, 'predominant');
    }
    return (_a = (0, predominant_species_1.getPredominantSpeciesByKey)(result)) === null || _a === void 0 ? void 0 : _a.tupuake_id;
};
exports.getPredominantSpecieIdOrThrowError = getPredominantSpecieIdOrThrowError;
const convertSegmentationToTupuAkeGeoJson = (segmentation) => {
    const tupuake_segmentation = [];
    let sid = 1;
    const getThenIncrementSid = () => {
        sid += 1;
        return sid - 1;
    };
    const registered_segmentation = {
        type: 'FeatureCollection',
        features: segmentation.features.filter((feature) => { var _a; return ((_a = feature.properties) === null || _a === void 0 ? void 0 : _a.status) === 'registered'; }),
    };
    registered_segmentation.features.forEach((feature) => {
        var _a, _b, _c;
        if (!feature.properties) {
            throw new Error('Feature has no properties');
        }
        if (!isPlantingOperation(feature.properties)) {
            const species = feature.properties.species;
            const tree_specie_id = (0, exports.getTreeSpecieIdOrThrowError)(species);
            const predominant_specie_id = (0, exports.getPredominantSpecieIdOrThrowError)(species);
            const ets_species = feature.properties.ets_species;
            const forest_type_id = (0, forest_type_1.getForestTypeByKey)(ets_species).tupuake_forest_type_id;
            const establishment_method_id = (0, establishment_method_1.getEstablishmentMethodByKey)(feature.properties.establishment_method).tupuake_id;
            const clearance_type_id = (0, clearance_method_1.getClearanceMethodByKey)(feature.properties.clearance_method).tupuake_id;
            tupuake_segmentation.push((0, exports.generateClearingFeature)({
                sid: getThenIncrementSid(),
                clearance_type_id: clearance_type_id,
                previous_deforestation_date: (_a = feature.properties.previous_deforestation_date) !== null && _a !== void 0 ? _a : '',
                geometry: feature.geometry,
            }));
            tupuake_segmentation.push((0, exports.generatePlantingFeature)({
                sid: getThenIncrementSid(),
                establishment_method_id: establishment_method_id,
                forest_type_id: forest_type_id,
                predominant_specie_id: predominant_specie_id,
                tree_specie_id: tree_specie_id,
                stem_count: (_b = feature.properties.stem_count) !== null && _b !== void 0 ? _b : null,
                established_date: (_c = feature.properties.established_date) !== null && _c !== void 0 ? _c : '',
                geometry: feature.geometry,
            }));
        }
    });
    return (0, helpers_1.featureCollection)(tupuake_segmentation);
};
exports.convertSegmentationToTupuAkeGeoJson = convertSegmentationToTupuAkeGeoJson;
